import type { BlockType } from '@/api/courses';

/**
 * Block types that receive a visible exercise number (1.1, 1.2, …) in both the
 * builder and the student player — interactive TASKS only. Pure media,
 * decorative and vocab blocks are shown without a number, matching Edvibe
 * (e.g. the intro image / YouTube clip above "1.1" in the reference lesson).
 */
export const NUMBERED_TYPES: ReadonlySet<BlockType> = new Set<BlockType>([
  'audio_record',
  'word_drag_to_gap', 'word_input_gap', 'word_form_select',
  'word_drag_to_image', 'word_input_image', 'word_form_image',
  'quiz_no_timer', 'quiz_with_timer',
  'true_false_ng',
  'sentence_order', 'column_sort', 'paragraph_order', 'anagram', 'matching',
  'essay', 'highlight',
  'table_fill', 'open_items',
]);

export function isNumbered(type: BlockType): boolean {
  return NUMBERED_TYPES.has(type);
}

/**
 * Minimal structural shapes so numbering works with BOTH the server `Section`
 * type and the builder's local `DraftSection` — they share `id` + `blocks[]`
 * with `{ id, type }`. We depend on nothing else on purpose.
 */
type NumberingBlock = { id: string; type: BlockType };
type NumberingSection = { id: string; blocks?: NumberingBlock[] };

/**
 * computeNumbers assigns each NUMBERED block a "<sectionNo>.<blockNo>" label.
 *
 *   - sectionNo: 1-based over the `sections` array in display order.
 *   - blockNo:   1-based running index of numbered blocks WITHIN that section;
 *                non-numbered blocks are skipped and consume no index.
 *
 * Returns a Map keyed by block id → label. Blocks that are not numbered are
 * simply absent from the map (callers render them without a badge).
 */
export function computeNumbers(sections: NumberingSection[]): Map<string, string> {
  const out = new Map<string, string>();
  sections.forEach((sec, si) => {
    let n = 0;
    (sec.blocks ?? []).forEach((b) => {
      if (!NUMBERED_TYPES.has(b.type)) return;
      n += 1;
      out.set(b.id, `${si + 1}.${n}`);
    });
  });
  return out;
}
